import { createAccessControl } from "better-auth/plugins/access";
import { PERMISSION_STATEMENTS } from "@jurni/contracts/permissions";
import type { McpPermissions, PermissionKey, PermissionStatement, Permissions } from "@jurni/contracts/permissions";

export { PERMISSION_STATEMENTS } from "@jurni/contracts/permissions";
export type { McpPermissions, PermissionKey, PermissionStatement, Permissions } from "@jurni/contracts/permissions";

export const ac = createAccessControl(PERMISSION_STATEMENTS);

export function isOrganizationOwner(role: string): boolean {
  return role.split(",").includes("owner");
}

export const staff = ac.newRole({
  invitation: [],
  sensitiveData: ["read"],
  basic: ["manage"],
  shop: ["read"],
  metrics: ["read"],
  experiments: ["read", "write"],
  aiDiscovery: ["read", "write"],
  billing: [],
  aiChat: ["read", "write"],
  sections: ["read", "write"],
  buyBoxes: ["read", "write"],
  figma: ["read"],
  googleAds: ["read"],
  mediaSources: ["read", "write"],
  messaging: ["read"],
  funnels: ["read", "write", "comment"],
  smartLinks: ["read", "write"],
  roadmap: ["read", "write"],
  lpInspiration: ["read", "write"],
  systemConfig: [],
  shopifyWebhooks: [],
});

export const owner = ac.newRole({
  invitation: ["create", "cancel"],
  member: ["create", "update", "delete"],
  sensitiveData: ["read"],
  basic: ["manage"],
  shop: ["read"],
  metrics: ["read"],
  experiments: ["read", "write"],
  aiDiscovery: ["read", "write"],
  billing: ["read"],
  aiChat: ["read", "write"],
  sections: ["read", "write"],
  buyBoxes: ["read", "write"],
  figma: ["read", "write"],
  googleAds: ["read", "write"],
  mediaSources: ["read", "write"],
  messaging: ["read", "write"],
  funnels: ["read", "write", "comment"],
  smartLinks: ["read", "write"],
  roadmap: ["read", "write"],
  lpInspiration: ["read", "write"],
  systemConfig: [],
  shopifyWebhooks: [],
});

export const admin = ac.newRole({
  invitation: ["create", "cancel"],
  member: ["delete"],
  sensitiveData: ["read"],
  basic: ["manage"],
  shop: ["read"],
  metrics: ["read"],
  experiments: ["read", "write"],
  aiDiscovery: ["read", "write"],
  billing: ["read"],
  aiChat: ["read", "write"],
  sections: ["read", "write"],
  buyBoxes: ["read", "write"],
  figma: ["read", "write"],
  googleAds: ["read", "write"],
  mediaSources: ["read", "write"],
  messaging: ["read", "write"],
  funnels: ["read", "write", "comment"],
  smartLinks: ["read", "write"],
  roadmap: ["read", "write"],
  lpInspiration: ["read", "write"],
  systemConfig: [],
  shopifyWebhooks: [],
});

export const designer = ac.newRole({
  sensitiveData: [],
  basic: ["manage"],
  shop: ["read"],
  metrics: [],
  experiments: [],
  aiDiscovery: [],
  billing: [],
  aiChat: ["read", "write"],
  sections: ["read", "write"],
  buyBoxes: ["read", "write"],
  figma: ["read"],
  googleAds: [],
  mediaSources: [],
  messaging: ["read"],
  funnels: ["read", "write", "comment"],
  smartLinks: [],
  roadmap: ["read"],
  lpInspiration: ["read", "write"],
  systemConfig: [],
  shopifyWebhooks: [],
});

export const user = ac.newRole({
  sensitiveData: [],
  basic: [],
  shop: [],
  metrics: [],
  experiments: [],
  aiDiscovery: [],
  aiChat: [],
  sections: [],
  buyBoxes: [],
  figma: [],
  googleAds: [],
  mediaSources: [],
  messaging: [],
  funnels: [],
  smartLinks: [],
  roadmap: [],
  lpInspiration: [],
  systemConfig: [],
  shopifyWebhooks: [],
});

export const superDesigner = ac.newRole({
  sensitiveData: [],
  basic: [],
  shop: [],
  metrics: [],
  experiments: [],
  aiDiscovery: [],
  aiChat: [],
  sections: [],
  buyBoxes: [],
  figma: [],
  googleAds: [],
  mediaSources: [],
  messaging: [],
  funnels: [],
  smartLinks: [],
  roadmap: [],
  lpInspiration: [],
  systemConfig: [],
  shopifyWebhooks: [],
});

type MutableMcpPermissions = {
  -readonly [K in keyof McpPermissions]?: NonNullable<McpPermissions[K]>;
};

/**
 * MCP permission subset — only these resources/actions are exposed to MCP endpoints.
 * `satisfies` validates the shape while preserving the exact literal action types.
 */
const MCP_PERMISSION_SUBSET = {
  shop: ["read"],
  metrics: ["read"],
  funnels: ["read", "write"],
  smartLinks: ["read", "write"],
  experiments: ["read", "write"],
} satisfies Required<McpPermissions>;

const roleStatements = {
  staff: staff.statements,
  owner: owner.statements,
  admin: admin.statements,
  designer: designer.statements,
  user: user.statements,
};

type RoleName = keyof typeof roleStatements;

function isRoleName(roleName: string): roleName is RoleName {
  return roleName in roleStatements;
}

export function roleHasPermission<K extends PermissionKey>(
  roleName: string,
  resource: K,
  action: PermissionStatement[K][number],
): boolean {
  if (!isRoleName(roleName)) return false;
  const statements: Partial<Record<PermissionKey, readonly string[]>> = roleStatements[roleName];
  return statements[resource]?.includes(action) ?? false;
}

function getGrantedPermissionActions<T extends string>(
  roleActions: readonly string[] | undefined,
  allowedActions: readonly T[],
): T[] | undefined {
  if (!roleActions) return undefined;

  const grantedActions: T[] = [];
  for (const action of allowedActions) {
    if (roleActions.includes(action)) {
      grantedActions.push(action);
    }
  }
  return grantedActions.length > 0 ? grantedActions : undefined;
}

export function resolveMcpPermissions(roleName: string): McpPermissions {
  if (!isRoleName(roleName)) return {};

  const statements = roleStatements[roleName];
  const allowed: MutableMcpPermissions = {};

  const metrics = getGrantedPermissionActions(statements.metrics, MCP_PERMISSION_SUBSET.metrics);
  if (metrics) {
    allowed.metrics = metrics;
  }

  const shop = getGrantedPermissionActions(statements.shop, MCP_PERMISSION_SUBSET.shop);
  if (shop) {
    allowed.shop = shop;
  }

  const funnels = getGrantedPermissionActions(statements.funnels, MCP_PERMISSION_SUBSET.funnels);
  if (funnels) {
    allowed.funnels = funnels;
  }

  const smartLinks = getGrantedPermissionActions(statements.smartLinks, MCP_PERMISSION_SUBSET.smartLinks);
  if (smartLinks) {
    allowed.smartLinks = smartLinks;
  }

  const experiments = getGrantedPermissionActions(statements.experiments, MCP_PERMISSION_SUBSET.experiments);
  if (experiments) {
    allowed.experiments = experiments;
  }

  return allowed;
}
